'use client';

import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { User, ChevronDown } from 'lucide-react';
import { getCurrentUserSession } from '@/app/actions/auth';
import { SESSION_CHANGED_EVENT } from '@/lib/session-change';
import { getUserPropertiesServer } from '@/app/actions/property';
import type { SessionInfo } from './AccountMenuPanel';

// The entries load when the menu is about to open; the header on every page
// carries only the button (speed, 2026-09-25).
const loadPanel = () => import('./AccountMenuPanel');
const AccountMenuPanel = dynamic(loadPanel, {
  ssr: false,
  loading: () => <div className="p-3 text-xs text-neutral-400">{document.documentElement.lang === 'fr' ? 'Chargement...' : 'Loading...'}</div>,
});
let preloading: Promise<unknown> | null = null;
const preloadPanel = () => {
  preloading ??= loadPanel();
};

// Account dropdown in the site header. Its entries depend on who is signed in:
// guests see sign-in, sellers their listings, agencies their dashboard and site,
// admins the admin area.
export default function AccountMenu({ lang }: { lang: string }) {
  const isFr = lang === 'fr';
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState<SessionInfo | undefined>(undefined);
  const [listingsCount, setListingsCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const pathname = usePathname();

  // Closes itself when the page changes. On a phone it covers the top of the
  // page, and on a slow first load it sat there over the page it had opened.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    let cancelled = false;
    const readSession = () => getCurrentUserSession()
      .then(async (s) => {
        if (cancelled) return;
        setSession(s ? { role: s.role, email: s.email, userId: s.userId, agencyRole: s.agencyRole } : null);
        if (s && s.role !== 'admin') {
          const res = await getUserPropertiesServer();
          if (!cancelled && res.success) setListingsCount(res.properties?.length || 0);
        }
      })
      .catch(() => !cancelled && setSession(null));
    readSession();
    window.addEventListener(SESSION_CHANGED_EVENT, readSession);

    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      cancelled = true;
      window.removeEventListener(SESSION_CHANGED_EVENT, readSession);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Escape closes the menu and returns focus to its button - only while the
  // menu is open. Listening all the time pulled focus into the header whenever
  // Escape closed anything else on the page.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const close = () => setOpen(false);

  const buttonLabel = session
    ? (isFr ? 'Mon compte' : 'My account')
    : (isFr ? 'Se connecter' : 'Sign in');

  return (
    <div className="relative" ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(!open)}
        onMouseEnter={preloadPanel}
        onFocus={preloadPanel}
        onTouchStart={preloadPanel}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-neutral-800 bg-neutral-900 text-neutral-300 hover:bg-neutral-800 text-xs sm:text-sm font-semibold transition cursor-pointer shadow-sm"
      >
        <User className="w-4 h-4 text-champagne" />
        <span className="max-lg:sr-only">{buttonLabel}</span>
        {/* Dark on the bright green rather than white: the green is unchanged,
            but 9px white on it read at 2.47:1. */}
        {listingsCount > 0 && (
          <span className="w-4 h-4 rounded-full bg-success text-black text-[10px] font-mono font-bold flex items-center justify-center">
            {listingsCount}
          </span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        // A tap anywhere else closes it. The document listener below does the
        // same, but only where a tap reaches the document: this layer is over
        // the page (and under the menu), so nothing can swallow that tap.
        <span aria-hidden="true" onClick={close} className="sm:hidden fixed inset-0 z-40" />
      )}

      {open && (
        // On a phone the menu spans the screen under the 64px header, inside
        // the 10px gutters: anchored to its button it was 288px wide ending
        // where the button ends, with the EN/FR switch to its right, so it ran
        // off the left edge. From sm up it hangs from the button as before.
        <div className="fixed left-2.5 right-2.5 top-[4.25rem] max-h-[calc(100dvh-5rem)] overflow-y-auto sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 sm:max-h-none sm:overflow-visible bg-neutral-900 border border-neutral-800 rounded-2xl shadow-2xl p-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
          <AccountMenuPanel lang={lang} session={session} listingsCount={listingsCount} close={close} />
        </div>
      )}
    </div>
  );
}
